import { useState } from "preact/hooks";
import { useNavigate } from "../contexts/AppContext";
import { PublicLayout } from "./PublicLayout";

// Login + signup share a single component since the form shape is
// identical and the only differences are the endpoint and the link to
// the other mode.

type Mode = "login" | "signup";

export function Login({
  mode,
  onAuthed,
  onVerificationRequired,
}: {
  mode: Mode;
  onAuthed: (email: string) => void;
  // Called when the server responds with verification_required: the
  // SPA flips to the code-entry screen, carrying the email forward.
  onVerificationRequired: (email: string) => void;
}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // FT-POLICIES: signup requires explicit ToS+Privacy consent.
  const [tosAccepted, setTosAccepted] = useState(false);

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    if (mode === "signup" && !tosAccepted) {
      setError("Please accept the Terms and Privacy Policy.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`/api/auth/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Failed");
        return;
      }
      const data = await res.json();
      // FT-AUTH: signup always goes through verification; login only
      // when the account hasn't been verified yet. Either way the
      // server signals it the same way and we route to /verify.
      if (data.verification_required) {
        onVerificationRequired(data.email || email.trim());
        return;
      }
      onAuthed(data.user.email);
      // After auth, return to the page the user originally wanted (if
      // any), otherwise the home list. The redirect helper that drove
      // this stamps ?next= onto /login.
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next") || "/";
      navigate(next);
    } catch (err) {
      setError(String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const otherPath = mode === "login" ? "/signup" : "/login";
  const otherLabel = mode === "login" ? "Create one" : "Log in";
  const heading = mode === "login" ? "Log in" : "Sign up";

  return (
    <PublicLayout>
      <div class="container py-5">
        <div class="row justify-content-center">
          <div class="col-md-6 col-lg-5">
            <div class="card shadow-sm">
              <div class="card-body p-4">
                <h1 class="h3 mb-4 text-center">{heading}</h1>
                <form onSubmit={submit}>
                  <div class="mb-3">
                    <label class="form-label">Email</label>
                    <input
                      type="email"
                      class="form-control"
                      value={email}
                      onInput={(e) =>
                        setEmail((e.target as HTMLInputElement).value)
                      }
                      autoFocus
                      required
                    />
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Password</label>
                    <input
                      type="password"
                      class="form-control"
                      value={password}
                      onInput={(e) =>
                        setPassword((e.target as HTMLInputElement).value)
                      }
                      minLength={mode === "signup" ? 8 : undefined}
                      required
                    />
                    {mode === "signup" && (
                      <div class="form-text">At least 8 characters.</div>
                    )}
                  </div>
                  {mode === "signup" && (
                    <div class="mb-3 form-check">
                      <input
                        id="tos"
                        type="checkbox"
                        class="form-check-input"
                        checked={tosAccepted}
                        onChange={(e) =>
                          setTosAccepted((e.target as HTMLInputElement).checked)
                        }
                        required
                      />
                      <label htmlFor="tos" class="form-check-label small">
                        I agree to the{" "}
                        <a href="/legal/terms" target="_blank" rel="noreferrer">
                          Terms
                        </a>{" "}
                        and{" "}
                        <a
                          href="/legal/privacy"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Privacy Policy
                        </a>
                        .
                      </label>
                    </div>
                  )}
                  {error && <div class="alert alert-danger py-2">{error}</div>}
                  <button
                    type="submit"
                    class="btn btn-primary w-100"
                    disabled={submitting}
                  >
                    <i class="bi bi-box-arrow-in-right"></i>{" "}
                    {submitting ? "..." : heading}
                  </button>
                </form>
                {mode === "login" && (
                  <div class="text-center mt-3 small">
                    <a
                      href="/forgot"
                      onClick={(e: Event) => {
                        e.preventDefault();
                        navigate("/forgot");
                      }}
                    >
                      Forgot password?
                    </a>
                  </div>
                )}
                <div class="text-center mt-3 small">
                  {mode === "login" ? "No account? " : "Have an account? "}
                  <a
                    href={otherPath}
                    onClick={(e: Event) => {
                      e.preventDefault();
                      navigate(otherPath);
                    }}
                  >
                    {otherLabel}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
